import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, BookOpen, Clock, Users, Award, Edit, Check, X } from 'lucide-react';
import './Overview.css';
import { apiFetch } from '../../utils/api';

const Overview = () => {
  const { name } = useParams();
  const learningPathId = name.split("-").pop();
  const userEmail = localStorage.getItem('userEmail');

  const [learningPath, setLearningPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editedDesc, setEditedDesc] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSavingDesc, setIsSavingDesc] = useState(false);

  useEffect(() => {
    if (!userEmail) {
      setError("User email not found in localStorage. Please log in.");
      setLoading(false);
      return;
    }

    const fetchLearningPath = async () => {
      try {
        const data = await apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}?user_email=${encodeURIComponent(userEmail)}`);
        setLearningPath(data);
        setEditedName(data.name);
        setEditedDesc(data.description || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLearningPath();
  }, [learningPathId, userEmail]);

  const handleSaveName = async () => {
    if (editedName === learningPath.name) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);
    try {
      await apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: editedName }),
      });
      const updatedData = await apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}?user_email=${encodeURIComponent(userEmail)}`);
      setLearningPath(updatedData);
      setIsEditingName(false);
      showMessage('Name updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleSaveDesc = async () => {
    if (editedDesc === learningPath.description) {
      setIsEditingDesc(false);
      return;
    }

    setIsSavingDesc(true);
    try {
      await apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ description: editedDesc }),
      });
      const updatedData = await apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}?user_email=${encodeURIComponent(userEmail)}`);
      setLearningPath(updatedData);
      setIsEditingDesc(false);
      showMessage('Description updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSavingDesc(false);
    }
  };

  const showMessage = (message) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
    setTimeout(() => {
      setShowSnackbar(false);
    }, 3000);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!learningPath) return <div>No data found</div>;

  return (
    <div className="mplo-overview-container">
      <div className="mplo-overview-header">
        {/* Title Section */}
        <div className="mplo-title-section">
          {isEditingName ? (
            <div className="mplo-inline-edit">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="mplo-title-input"
                autoFocus
              />
              <div className="mplo-inline-actions">
                <button
                  className="mplo-action-btn mplo-save-btn"
                  onClick={handleSaveName}
                  disabled={isSavingName}
                >
                  <Check size={14} />
                  {isSavingName ? 'Saving...' : ''}
                </button>
                <button
                  className="mplo-action-btn mplo-cancel-btn"
                  onClick={() => {
                    setIsEditingName(false);
                    setEditedName(learningPath.name);
                  }}
                  disabled={isSavingName}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="mplo-display-row">
              <h1>{learningPath.name}</h1>
              <Edit
                size={16}
                className="mplo-edit-icon"
                onClick={() => setIsEditingName(true)}
              />
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className="mplo-desc-section">
          {isEditingDesc ? (
            <div className="mplo-inline-edit">
              <textarea
                value={editedDesc}
                onChange={(e) => setEditedDesc(e.target.value)}
                className="mplo-desc-textarea"
                autoFocus
                rows={3}
              />
              <div className="mplo-inline-actions">
                <button
                  className="mplo-action-btn mplo-save-btn"
                  onClick={handleSaveDesc}
                  disabled={isSavingDesc}
                >
                  <Check size={14} />
                  {isSavingDesc ? 'Saving...' : ''}
                </button>
                <button
                  className="mplo-action-btn mplo-cancel-btn"
                  onClick={() => {
                    setIsEditingDesc(false);
                    setEditedDesc(learningPath.description || '');
                  }}
                  disabled={isSavingDesc}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="mplo-display-row mplo-desc-row">
              <p>{learningPath.description || 'No description provided'}</p>
              <Edit
                size={16}
                className="mplo-edit-icon"
                onClick={() => setIsEditingDesc(true)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mplo-overview-stats">
        <div className="mplo-stat-item">
          <div className="mplo-stat-icon mplo-icon-blue">
            <Calendar size={16} />
          </div>
          <div className="mplo-stat-content">
            <span className="mplo-stat-label">Start Date</span>
            <span className="mplo-stat-value">{learningPath.start_date || 'N/A'}</span>
          </div>
        </div>
        <div className="mplo-stat-item">
          <div className="mplo-stat-icon mplo-icon-purple">
            <Calendar size={16} />
          </div>
          <div className="mplo-stat-content">
            <span className="mplo-stat-label">End Date</span>
            <span className="mplo-stat-value">{learningPath.end_date || 'N/A'}</span>
          </div>
        </div>
        <div className="mplo-stat-item">
          <div className="mplo-stat-icon mplo-icon-blue2">
            <BookOpen size={16} />
          </div>
          <div className="mplo-stat-content">
            <span className="mplo-stat-label">Total Courses</span>
            <span className="mplo-stat-value">{learningPath.total_courses} Courses</span>
          </div>
        </div>
        <div className="mplo-stat-item">
          <div className="mplo-stat-icon mplo-icon-purple2">
            <Clock size={16} />
          </div>
          <div className="mplo-stat-content">
            <span className="mplo-stat-label">Total Duration</span>
            <span className="mplo-stat-value">{(learningPath.total_duration_hours || 0).toFixed(2)} hours</span>
          </div>
        </div>
        <div className="mplo-stat-item">
          <div className="mplo-stat-icon mplo-icon-gray">
            <Users size={16} />
          </div>
          <div className="mplo-stat-content">
            <span className="mplo-stat-label">Enrolled Users</span>
            <span className="mplo-stat-value">{learningPath.enrolled_users.toLocaleString()} learners</span>
          </div>
        </div>
        <div className="mplo-stat-item">
          <div className="mplo-stat-icon mplo-icon-blue3">
            <Award size={16} />
          </div>
          <div className="mplo-stat-content">
            <span className="mplo-stat-label">Total Credits</span>
            <span className="mplo-stat-value">{learningPath.total_credits}</span>
          </div>
        </div>
      </div>

      <div className="mplo-section-divider"></div>

      <div className="mplo-required-courses">
        <h3>Required Courses</h3>
        <div className="mplo-course-tags">
          {learningPath.courses && learningPath.courses.filter(c => c.is_mandatory).length > 0 ? (
            learningPath.courses
              .filter(course => course.is_mandatory)
              .map((course, index) => (
                <span key={`${course.course}-${course.is_external}-${index}`} className="mplo-course-tag">
                  {course.course} {course.is_external ? '(External)' : ''}
                </span>
              ))
          ) : (
            <span className="mplo-empty-text">No required courses</span>
          )}
        </div>
      </div>

      <div className="mplo-section-divider"></div>

      <div className="mplo-course-dashboard">
        <h2>Course Dashboard</h2>
        <div className="mplo-table-container">
          <table className="mplo-course-table">
            <thead>
              <tr>
                <th>Course</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Credits</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              {learningPath.courses && learningPath.courses.length > 0 ? (
                learningPath.courses.map((course, index) => (
                  <tr key={`${course.course}-${course.start_date}-${course.end_date}-${index}`}>
                    <td>
                      <div className="mplo-course-info">
                        <div className="mplo-course-title">
                          {course.course} {course.is_external ? <span className="mmlp-external-span">(External)</span> : ''}
                        </div>
                        <div className="mplo-course-duration">
                          {course.is_external
                            ? `${course.estimated_duration || 'N/A'} hours`
                            : `${course.video_count} videos \u2022 ${course.duration}`}
                        </div>
                      </div>
                    </td>
                    <td>{course.start_date || 'N/A'}</td>
                    <td>{course.end_date || 'N/A'}</td>
                    <td>{course.credits}</td>
                    <td>
                      <span
                        className={`mplo-required-badge ${course.is_mandatory ? 'mplo-required-yes' : 'mplo-required-no'}`}
                      >
                        {course.is_mandatory ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="mplo-empty-text">
                    No courses present in this learning path
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showSnackbar && (
        <div className="mplo-snackbar">
          {snackbarMessage}
        </div>
      )}
    </div>
  );
};

export default Overview;